import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFilterQueryState } from 'src/components/MainFilterBar/useFilterQueryState';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { Button } from 'src/core/ui';
import { useCatchUpModeEventIdsMap } from 'src/hooks/api/useCacheUpModeEventIdsMap';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { TAB_EVENT_IDS_MAPPING_ID } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  CatchUpModeSource,
  CommandOptionType,
  ListingQuery,
} from 'src/WebApiController';

export const useCatchupAction = () => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const navigate = useNavigate();
  const { selectedViagogoVirtualIds } = useFilterQueryState<ListingQuery>(
    ActionOutboxEntityType.Listing
  );
  const { onSaveCatchUpModeEventIds, isLoading } = useCatchUpModeEventIdsMap();

  const onCatchUp = useCallback(async () => {
    const cacheId = await onSaveCatchUpModeEventIds(
      CatchUpModeSource.InventoryEvents,
      selectedViagogoVirtualIds ?? []
    );

    const catchUpEventUrl = `/inventory/event?${TAB_EVENT_IDS_MAPPING_ID}=${cacheId}`;
    navigate(catchUpEventUrl);
  }, [navigate, onSaveCatchUpModeEventIds, selectedViagogoVirtualIds]);

  const onCatchupStableRef = useEventCallback(() => {
    onCatchUp();
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingCatchupMode,
        onSelect: onCatchupStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingCatchupMode,
      });
    };
  }, [onCatchupStableRef, registerCommand, unregisterCommand]);

  const disabled = useMemo(() => {
    if (!selectedViagogoVirtualIds?.length) {
      return true;
    }

    return isLoading;
  }, [isLoading, selectedViagogoVirtualIds?.length]);

  return {
    dropdown: (
      <Button variant="outline" onClick={onCatchUp} disabled={disabled}>
        <Content id={ContentId.Catchup} />
      </Button>
    ),
  };
};
